import React from "react";
import { Printer } from "lucide-react";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export function DraftContractPreview({ formData, selectedTemplate, open, onOpenChange }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return "_______________";
    return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  };

  const formatCurrency = (val) => {
    if (!val) return "_______________";
    const currency = formData.currency || formData.template_metadata?.currency || "GBP";
    return new Intl.NumberFormat("en-GB", { style: "currency", currency, minimumFractionDigits: 2 }).format(parseFloat(val));
  };

  const handlePrint = () => {
    const printContent = document.getElementById("draft-contract-printable");
    if (!printContent) return;
    const html = `<!DOCTYPE html><html><head><title>${(formData.title || "Draft Contract").replace(/[<>"'&]/g, "")}</title>
      <style>
        body { font-family: 'Georgia', 'Times New Roman', serif; color: #1a1a1a; max-width: 800px; margin: 40px auto; padding: 0 40px; line-height: 1.7; font-size: 13px; }
        h1 { text-align: center; font-size: 22px; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 1px; }
        h2 { font-size: 16px; margin-top: 28px; margin-bottom: 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
        h3 { font-size: 14px; margin-top: 20px; margin-bottom: 6px; }
        .meta-row { display: flex; justify-content: space-between; margin-bottom: 4px; }
        .meta-label { font-weight: bold; min-width: 160px; }
        .parties { border: 1px solid #ddd; padding: 16px; margin: 16px 0; border-radius: 4px; }
        .section-divider { border-top: 1px solid #ccc; margin: 24px 0; }
        .section-content { white-space: pre-wrap; }
        .badge { display: inline-block; padding: 2px 8px; font-size: 10px; border: 1px solid #888; border-radius: 3px; margin-left: 8px; vertical-align: middle; }
        .watermark { text-align: center; color: #bbb; font-size: 11px; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 20px; }
        .signature-block { display: flex; justify-content: space-between; margin-top: 60px; }
        .sig-line { width: 45%; }
        .sig-line hr { margin-top: 60px; border: none; border-top: 1px solid #333; }
        @media print { body { margin: 20px; } }
      </style></head><body>${printContent.innerHTML}</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    win.onload = () => { URL.revokeObjectURL(url); win.print(); };
  };

  const party = formData.contracting_party || {};
  const hasSections = formData.sections?.length > 0;
  const hasAdditionalClauses = formData.additional_clauses?.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b">
          <DialogHeader className="space-y-0">
            <DialogTitle className="text-lg">Draft Contract Preview</DialogTitle>
            <p className="sr-only">Preview of the contract document with all details filled in</p>
          </DialogHeader>
          <Button onClick={handlePrint} variant="outline" className="gap-2 rounded-sm" data-testid="print-draft-btn">
            <Printer size={15} />
            Print / Export
          </Button>
        </div>

        <ScrollArea className="max-h-[calc(92vh-80px)]">
          <div id="draft-contract-printable" className="px-10 py-8" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", color: "#1a1a1a", lineHeight: 1.75, fontSize: "13.5px" }}>
            {/* Watermark */}
            <p style={{ textAlign: "center", color: "#bbb", fontSize: "11px", textTransform: "uppercase", letterSpacing: "3px", marginBottom: "16px" }}>
              Draft — Not for Execution
            </p>

            {/* Title */}
            <h1 style={{ textAlign: "center", fontSize: "22px", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 700 }}>
              {formData.title || "Untitled Contract"}
            </h1>
            {formData.contract_type && (
              <p style={{ textAlign: "center", color: "#666", fontSize: "13px", marginBottom: "24px" }}>
                {formData.contract_type}
              </p>
            )}

            {/* Parties */}
            <div style={{ border: "1px solid #ddd", padding: "16px 20px", margin: "20px 0", borderRadius: "4px", background: "#fafafa" }}>
              <p style={{ fontWeight: 700, marginBottom: "12px", fontSize: "14px" }}>Parties to this Agreement</p>

              <div style={{ display: "flex", gap: "32px" }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, fontSize: "12px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>The Client</p>
                  <p>{formData.template_metadata?.company_name || selectedTemplate?.template_metadata?.company_name || formData.contract_owner_name || "[Client Name]"}</p>
                  {(formData.template_metadata?.company_address || selectedTemplate?.template_metadata?.company_address || formData.department) && (
                    <p style={{ color: "#555", fontSize: "12.5px" }}>{formData.template_metadata?.company_address || selectedTemplate?.template_metadata?.company_address || formData.department}</p>
                  )}
                  {formData.contract_owner_email && <p style={{ color: "#555", fontSize: "12.5px" }}>{formData.contract_owner_email}</p>}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, fontSize: "12px", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>The Supplier / Counterparty</p>
                  <p>{party.legal_name || "[Counterparty Name]"}</p>
                  {party.registered_address && <p style={{ color: "#555", fontSize: "12.5px" }}>{party.registered_address}</p>}
                  {party.contact_email && <p style={{ color: "#555", fontSize: "12.5px" }}>{party.contact_email}</p>}
                  {party.contact_phone && <p style={{ color: "#555", fontSize: "12.5px" }}>{party.contact_phone}</p>}
                </div>
              </div>
            </div>

            {/* Sections */}
            {hasSections && formData.sections.map((section, index) => (
              <div key={section.id || index}>
                <div style={{ borderTop: "1px solid #ccc", margin: "24px 0" }} />
                <h2 style={{ fontSize: "15px", marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                  {section.name}
                  {!section.is_editable && (
                    <span style={{ display: "inline-block", padding: "1px 7px", fontSize: "10px", border: "1px solid #999", borderRadius: "3px", color: "#777", verticalAlign: "middle" }}>
                      Standard Terms
                    </span>
                  )}
                </h2>
                <p style={{ whiteSpace: "pre-wrap", fontSize: "13px" }}>{section.content || "[No content]"}</p>
              </div>
            ))}

            {/* Free-form content */}
            {!hasSections && formData.content && (
              <>
                <div style={{ borderTop: "1px solid #ccc", margin: "24px 0" }} />
                <p style={{ whiteSpace: "pre-wrap" }}>{formData.content}</p>
              </>
            )}

            {/* Additional Clauses */}
            {hasAdditionalClauses && (
              <>
                <div style={{ borderTop: "1px solid #ccc", margin: "24px 0" }} />
                <h2 style={{ fontSize: "15px", marginBottom: "8px" }}>Additional Clauses</h2>
                {formData.additional_clauses.map((clause, index) => (
                  <div key={clause.id || index} style={{ marginBottom: "16px" }}>
                    <h3 style={{ fontSize: "13.5px", fontWeight: 600, marginBottom: "2px" }}>
                      {clause.name}
                      <span style={{ display: "inline-block", padding: "1px 7px", fontSize: "10px", border: "1px solid #999", borderRadius: "3px", marginLeft: "8px", color: "#777" }}>
                        {clause.category}
                      </span>
                    </h3>
                    <p style={{ whiteSpace: "pre-wrap", fontSize: "13px" }}>{clause.content}</p>
                  </div>
                ))}
              </>
            )}

            {/* Key Terms */}
            <div style={{ borderTop: "1px solid #ccc", margin: "24px 0" }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", fontSize: "13px" }}>
              <div style={{ display: "flex", gap: "8px" }}><span style={{ fontWeight: 600, minWidth: "140px" }}>Contract Value:</span><span>{formatCurrency(formData.contract_value)}</span></div>
              <div style={{ display: "flex", gap: "8px" }}><span style={{ fontWeight: 600, minWidth: "140px" }}>Department:</span><span>{formData.department || "_______________"}</span></div>
              <div style={{ display: "flex", gap: "8px" }}><span style={{ fontWeight: 600, minWidth: "140px" }}>Effective Date:</span><span>{formatDate(formData.effective_date)}</span></div>
              <div style={{ display: "flex", gap: "8px" }}><span style={{ fontWeight: 600, minWidth: "140px" }}>Expiration Date:</span><span>{formatDate(formData.expiration_date)}</span></div>
            </div>

            {/* Deliverables Table */}
            {formData.deliverables?.length > 0 && (
              <>
                <div style={{ borderTop: "1px solid #ccc", margin: "24px 0" }} />
                <h2 style={{ fontSize: "15px", marginBottom: "8px" }}>Schedule of Deliverables</h2>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #ccc" }}>
                      <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600, width: "40px" }}>#</th>
                      <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>Deliverable</th>
                      <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600, width: "150px" }}>Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.deliverables.map((del, i) => (
                      <tr key={del.id || i} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "6px 0" }}>{i + 1}</td>
                        <td style={{ padding: "6px 0" }}>{del.description || "[Not specified]"}</td>
                        <td style={{ padding: "6px 0" }}>{del.delivery_date ? formatDate(del.delivery_date) : "[TBD]"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* KPIs Table */}
            {formData.kpis?.length > 0 && (
              <>
                <div style={{ borderTop: "1px solid #ccc", margin: "24px 0" }} />
                <h2 style={{ fontSize: "15px", marginBottom: "8px" }}>Key Performance Indicators</h2>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #ccc" }}>
                      <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600, width: "40px" }}>#</th>
                      <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>KPI Description</th>
                      <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600, width: "200px" }}>Measure / Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.kpis.map((kpi, i) => (
                      <tr key={kpi.id || i} style={{ borderBottom: "1px solid #eee" }}>
                        <td style={{ padding: "6px 0" }}>{i + 1}</td>
                        <td style={{ padding: "6px 0" }}>{kpi.description || "[Not specified]"}</td>
                        <td style={{ padding: "6px 0" }}>{kpi.measure || "[Not specified]"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Signature Block */}
            <div style={{ borderTop: "1px solid #ccc", margin: "40px 0 0" }} />
            <p style={{ fontWeight: 700, fontSize: "14px", marginTop: "24px", marginBottom: "16px" }}>
              IN WITNESS WHEREOF, the Parties have executed this Agreement as of the date first written above.
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
              <div style={{ width: "45%" }}>
                <p style={{ fontWeight: 600, fontSize: "12px", color: "#666", textTransform: "uppercase", marginBottom: "4px" }}>For and on behalf of the Client</p>
                <p>{formData.template_metadata?.company_name || selectedTemplate?.template_metadata?.company_name || formData.contract_owner_name || "[Client Name]"}</p>
                <hr style={{ marginTop: "50px", border: "none", borderTop: "1px solid #333" }} />
                <p style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>Authorised Signatory</p>
                <p style={{ fontSize: "12px", color: "#666" }}>Date: _______________</p>
              </div>
              <div style={{ width: "45%" }}>
                <p style={{ fontWeight: 600, fontSize: "12px", color: "#666", textTransform: "uppercase", marginBottom: "4px" }}>For and on behalf of the Supplier</p>
                <p>{party.legal_name || "[Counterparty Name]"}</p>
                <hr style={{ marginTop: "50px", border: "none", borderTop: "1px solid #333" }} />
                <p style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>Authorised Signatory</p>
                <p style={{ fontSize: "12px", color: "#666" }}>Date: _______________</p>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
